import type Seat from "@/types/Mapping/Seat";
import type Section from "@/types/Section";
import type Sector from "@/types/Sector";
import type TicketType from "@/types/TicketType";
import type NominalTicket from "@/types/NominalTicket";
import type User from "@/types/User";
import type Event from "@/types/Event";
import Fee from "./fees";
import { cpf } from "./cpf";

/** One ticket in the Cart: a held seat with its type, or a General sector's ticket. */
export interface CartLine {
  /** Tells apart General tickets of the same type. */
  key: string;
  sector: Sector;
  section: Section;
  type: TicketType;
  /** The held seat, absent on a General ticket. */
  seat?: Seat;
}

/** The lines of one Section, as the Cart lists them. */
export interface CartGroup {
  sector: Sector;
  section: Section;
  lines: CartLine[];
}

/** Groups the lines by Section, in the order the buyer first added to each. */
export function groupLines(lines: CartLine[]): CartGroup[] {
  const groups = new Map<string, CartGroup>();

  for (const line of lines) {
    const group = groups.get(line.section.id) ?? { sector: line.sector, section: line.section, lines: [] };
    group.lines.push(line);
    groups.set(line.section.id, group);
  }

  return [ ...groups.values() ];
}

/**
 * The tickets of the lines, carrying each line's own Section, type and seat: one per line, or one per passport of a
 * type sold in packs (`passport_amount`). The first is in the buyer's name, the others wait for their holders' data.
 */
export function ticketsFromLines(lines: CartLine[], buyer: User): NominalTicket[] {
  const units = lines.flatMap(line => Array.from({ length: ticketsOf(line.type) }, () => line));

  return units.map((line, index): NominalTicket => {
    const first = index === 0;
    const batch = line.type.current_batch;

    return {
      name: first ? buyer.name : null,
      cpf: first ? (buyer.document_type === "passport" ? buyer.document : cpf.format(buyer.document)) : null,
      document_type: first ? buyer.document_type ?? "cpf" : "cpf",
      email: first ? buyer.email : undefined,
      cellphone: first ? buyer.cellphone : undefined,
      sector: line.sector,
      section: line.section,
      type: line.type,
      preco: (batch?.price ?? 0).toString(),
      batch_id: batch?.id ?? null,
      typeId: line.type.id,
      seat: line.seat,
      additional: line.type.additional_fields.map(field => ({ field, value: "" })),
    };
  });
}

/** A copy of the sectors with each type's amount (`quantidade`) taken from the lines, as the reservation sends them. */
export function reservationSectors(lines: CartLine[], sectors: Sector[]): Sector[] {
  const amounts = new Map<string, number>();
  lines.forEach(line => amounts.set(line.type.id, (amounts.get(line.type.id) ?? 0) + 1));

  return sectors.map(sector => ({
    ...sector,
    sections: sector.sections.map(section => ({
      ...section,
      tickets: section.tickets.map(type => ({ ...type, quantidade: amounts.get(type.id) ?? 0 })),
    })),
  }));
}

export interface CartTotals {
  subtotal: number;
  fees: number;
}

/** The prices of every ticket in the Cart, and their convenience fees, at the types' current batches. */
export function cartTotals(lines: CartLine[], event: Event): CartTotals {
  return lines.reduce((totals, line) => {
    const price = line.type.current_batch?.price ?? 0;

    return {
      subtotal: totals.subtotal + price,
      fees: totals.fees + Fee.calculate(price, event.convenience_fee, event.convenience_fee_mode),
    };
  }, { subtotal: 0, fees: 0 });
}

/** The amounts picked on a General sector's sheet, by type id, before they join the Cart. */
export interface GeneralPick {
  sector: Sector;
  section: Section;
  amounts: Record<string, number>;
}

/** Tickets `amount` of the type stand for: a type sold in packs is that many each (`passport_amount`), as the API counts them. */
export function ticketsOf(type: TicketType, amount = 1): number {
  return amount * Math.max(type.passport_amount, 1);
}

/**
 * How far the type's stepper goes on a General sector's sheet, given the Cart and the amounts already picked on the
 * sheet for the Section's other types.
 *
 * @param cap The buyer's cap for the event, counting seats and General tickets together.
 * @param waiting Type ids of the seats still waiting on their hold, which the Cart already counts.
 */
export function generalMax(type: TicketType, pick: GeneralPick, lines: CartLine[], cap: number, waiting: string[] = []): number {
  const batch = type.current_batch;

  // No batch on sale, no price to buy at
  if (batch == null) return 0;

  const others = pick.section.tickets.filter(t => t.id !== type.id);

  const pickedOthers = others.reduce((sum, t) => sum + (pick.amounts[t.id] ?? 0), 0);

  // Every ticket the cap already counts: the Cart's, the seats on their way into it and the sheet's other amounts
  const counted = lines.reduce((sum, line) => sum + ticketsOf(line.type), 0)
    + waiting.length
    + others.reduce((sum, t) => sum + ticketsOf(t, pick.amounts[t.id] ?? 0), 0);

  const ofType = lines.filter(line => line.type.id === type.id).length + waiting.filter(id => id === type.id).length;

  const inSector = lines.filter(line => line.sector.id === pick.sector.id).length + pickedOthers;

  const inSection = lines.filter(line => line.section.id === pick.section.id).length + pickedOthers;

  // A Random Section hands out its own seats, a General one has none
  const seats = pick.section.seats;

  const limits = [
    batch.quantity === null ? Infinity : batch.quantity - (batch.sold ?? 0) - ofType,
    pick.sector.capacity - pick.sector.sold - inSector,
    seats === undefined || seats.total === 0 ? Infinity : seats.available - inSection,
    Math.floor((cap - counted) / ticketsOf(type)),
    type.max_per_user === null ? Infinity : type.max_per_user - ofType,
  ];

  return Math.max(0, Math.min(...limits));
}
